/**
 * ColorScale.ts
 * -----------------------------------------------------------------------------
 * Bottom-center legend for the FE Results "Disp. Color Dir." contour: a wide
 * horizontal gradient bar (the same 7-stop scale FEMeshExporter.GetColor uses,
 * low=blue → high=red), a title, start/mid/end labels, and a hover tooltip showing the exact value under the cursor. Shown
 * only while contour coloring is active (Deformation mode with a direction
 * picked).
 */

import type { Viewport3D } from './Viewport3D';

// Mirrors FEMeshExporter.ColorScale (RGB 0..255) — keep these two in sync.
const STOPS: Array<[number, number, number]> = [
  [0, 0, 150],
  [0, 150, 150],
  [0, 150, 0],
  [255, 255, 0],
  [255, 128, 0],
  [255, 0, 255],
  [150, 0, 0],
];

/** Mirrors FEMeshExporter.GetColor exactly — same 7-stop interpolation, for
 *  client-side geometry (e.g. reaction arrows) that needs to color itself
 *  without a round trip through the C# exporter. Returns 0..1 RGB. */
export function getColor(value: number, min: number, range: number): [number, number, number] {
  const index = range !== 0 ? (6 * (value - min)) / range : 3;
  let colorIndex = Math.floor(index);

  if (colorIndex < 6) {
    if (colorIndex < 0) colorIndex = 0;
    const c1 = STOPS[colorIndex], c2 = STOPS[colorIndex + 1];
    const t = index - colorIndex;
    return [
      (t * (c2[0] - c1[0]) + c1[0]) / 255,
      (t * (c2[1] - c1[1]) + c1[1]) / 255,
      (t * (c2[2] - c1[2]) + c1[2]) / 255,
    ];
  }

  const c = STOPS[Math.min(colorIndex, STOPS.length - 1)];
  return [c[0] / 255, c[1] / 255, c[2] / 255];
}

export class ColorScale {
  private readonly el: HTMLDivElement;
  private readonly titleEl: HTMLDivElement;
  private readonly bar: HTMLDivElement;
  private readonly ticksEl: HTMLDivElement;
  private readonly tooltip: HTMLDivElement;
  private readonly marker: HTMLDivElement;
  private min = 0;
  private max = 0;

  constructor(viewport: Viewport3D) {
    this.el = document.createElement('div');
    this.el.className = 'bridge-colorscale hidden';

    this.titleEl = document.createElement('div');
    this.titleEl.className = 'bridge-colorscale-title';

    const barWrap = document.createElement('div');
    barWrap.className = 'bridge-colorscale-barwrap';

    this.bar = document.createElement('div');
    this.bar.className = 'bridge-colorscale-bar';
    this.bar.style.background = `linear-gradient(to right, ${STOPS.map((c) => `rgb(${c[0]},${c[1]},${c[2]})`).join(', ')})`;

    this.tooltip = document.createElement('div');
    this.tooltip.className = 'bridge-colorscale-tooltip hidden';

    this.marker = document.createElement('div');
    this.marker.className = 'bridge-colorscale-marker hidden';

    this.ticksEl = document.createElement('div');
    this.ticksEl.className = 'bridge-colorscale-ticks';

    barWrap.appendChild(this.bar);
    barWrap.appendChild(this.marker);
    barWrap.appendChild(this.tooltip);
    barWrap.appendChild(this.ticksEl);

    this.el.appendChild(this.titleEl);
    this.el.appendChild(barWrap);
    viewport.container.appendChild(this.el);

    this.bar.addEventListener('pointermove', (e) => this.onHover(e));
    this.bar.addEventListener('pointerleave', () => this.hideHover());
  }

  /**
   * Replace the gradient the bar shows. The FE contour palette above is the
   * default and stays in lockstep with FEMeshExporter; this exists for the other
   * things that have a range worth a legend and a scale of their own — an
   * imported map's terrain elevation, which runs low-blue to high-green.
   * Pass no argument to go back to the FE scale.
   */
  public setStops(stops?: ReadonlyArray<readonly [number, number, number]>): void {
    const use = stops && stops.length >= 2 ? stops : STOPS;
    this.bar.style.background =
      `linear-gradient(to right, ${use.map((c) => `rgb(${c[0]},${c[1]},${c[2]})`).join(', ')})`;
  }

  /** Show the legend for a min..max range under the given title (e.g. "Disp Z"). */
  public setScale(min: number, max: number, title: string): void {
    this.min = min;
    this.max = max;
    this.titleEl.textContent = title;
    this.buildTicks();
    this.el.classList.remove('hidden');
  }

  public clear(): void {
    this.el.classList.add('hidden');
    this.hideHover();
  }

  private hideHover(): void {
    this.tooltip.classList.add('hidden');
    this.marker.classList.add('hidden');
  }

  /** Shift the legend up by `px` (the clipping panel's height, when it's also
   *  docked at bottom-center) so the two overlays stack instead of overlapping.
   *  Pass 0 to return to the default CSS-defined position. */
  public setBottomOffset(px: number): void {
    this.el.style.bottom = px > 0 ? `${14 + px + 10}px` : '';
  }

  dispose(): void {
    if (this.el.parentElement) this.el.parentElement.removeChild(this.el);
  }

  private buildTicks(): void {
    this.ticksEl.innerHTML = '';

    // Three labels only — start, mid, end. Eleven ticks on a short bar ran into
    // each other once the values carried more than a couple of digits; the exact
    // value anywhere along the bar is what the hover tooltip is for.
    const span = Math.abs(this.max - this.min);
    for (const frac of [0, 0.5, 1]) {
      const value = this.min + frac * (this.max - this.min);

      const tickMark = document.createElement('span');
      tickMark.className = 'bridge-colorscale-tickmark';
      tickMark.style.left = `${frac * 100}%`;

      const label = document.createElement('span');
      label.className = 'bridge-colorscale-tick';
      label.style.left = `${frac * 100}%`;
      label.textContent = formatValue(value, span);
      if (frac === 0) label.style.transform = 'translateX(0)';
      else if (frac === 1) label.style.transform = 'translateX(-100%)';

      this.ticksEl.appendChild(tickMark);
      this.ticksEl.appendChild(label);
    }
  }

  private onHover(e: MouseEvent): void {
    const rect = this.bar.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const value = this.min + frac * (this.max - this.min);

    // One more digit than the tick labels, so the hover resolves finer than they do.
    this.tooltip.textContent = formatValue(value, Math.abs(this.max - this.min) / 10);
    this.tooltip.style.left = `${frac * 100}%`;
    this.marker.style.left = `${frac * 100}%`;
    this.marker.classList.remove('hidden');
    this.tooltip.style.transform =
      frac < 0.08 ? 'translateX(0)' : frac > 0.92 ? 'translateX(-100%)' : 'translateX(-50%)';
    this.tooltip.classList.remove('hidden');
  }
}

/** Format a value to the precision the scale's span calls for: three significant
 *  digits of the span, so a millimetre-range displacement keeps its decimals
 *  and a hundreds-of-feet elevation doesn't carry noise. Very large or very small
 *  magnitudes switch to exponent form so the label stays short. */
function formatValue(v: number, span: number): string {
  if (v === 0) return '0';
  const a = Math.abs(v);
  if (a >= 1e6 || (span > 0 ? span : a) < 1e-4) return v.toExponential(2);
  const ref = span > 0 ? span : a;
  const decimals = Math.max(0, Math.min(6, 2 - Math.floor(Math.log10(ref))));
  return v.toFixed(decimals);
}
